import React, { useEffect, useMemo, useState } from "react";
import { api, codeClasses } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MagnifyingGlass, X, Star, StarHalf } from "@phosphor-icons/react";

export default function ProtocolPicker({ selected, onSelect }) {
  const [q, setQ] = useState("");
  const [protocols, setProtocols] = useState([]);
  const [favs, setFavs] = useState([]);

  const load = async () => {
    const [p, f] = await Promise.all([api.get("/protocols"), api.get("/favorites")]);
    setProtocols(p.data);
    setFavs(f.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q) return protocols;
    const s = q.toLowerCase().trim();
    return protocols.filter((p) => {
      if (p.name.toLowerCase().includes(s)) return true;
      if ((p.category || "").toLowerCase().includes(s)) return true;
      return (p.keywords || []).some((k) => k.toLowerCase().includes(s));
    });
  }, [q, protocols]);

  const matchedKeyword = (p, s) => {
    if (!s || !p.keywords) return null;
    return p.keywords.find((k) => k.toLowerCase().includes(s));
  };

  const favSet = new Set(favs);
  const favProtos = protocols.filter((p) => favSet.has(p.protocol_id));

  const toggleFav = async (id, e) => {
    e.stopPropagation();
    const { data } = await api.post(`/favorites/${id}`);
    setFavs(data);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-lg font-semibold">Problema principale</h3>
        {selected && (
          <Badge variant="outline" className="font-mono text-xs">
            v.{selected.version}
          </Badge>
        )}
      </div>
      <div className="relative">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="protocol-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca sintomo, sinonimo o protocollo (es. sincope, mal di testa, tvp)"
          className="pl-10 pr-10 h-12 text-base"
        />
        {q && (
          <button data-testid="protocol-search-clear" onClick={() => setQ("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
            <X size={18} />
          </button>
        )}
      </div>

      {!q && favProtos.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2 flex items-center gap-1">
            <Star size={12} weight="fill" /> Preferiti
          </div>
          <div className="flex flex-wrap gap-2">
            {favProtos.map((p) => (
              <button
                key={p.protocol_id}
                data-testid={`fav-protocol-${p.protocol_id}`}
                onClick={() => onSelect(p)}
                className={`px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                  selected?.protocol_id === p.protocol_id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:bg-slate-50"
                }`}
              >{p.name}</button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 max-h-64 overflow-auto grid grid-cols-1 md:grid-cols-2 gap-2">
        {filtered.map((p) => {
          const active = selected?.protocol_id === p.protocol_id;
          const isFav = favSet.has(p.protocol_id);
          const kwMatch = matchedKeyword(p, q.toLowerCase().trim());
          const nameMatches = q && p.name.toLowerCase().includes(q.toLowerCase().trim());
          return (
            <div
              key={p.protocol_id}
              data-testid={`protocol-item-${p.protocol_id}`}
              onClick={() => onSelect(p)}
              className={`cursor-pointer flex items-center justify-between px-4 py-3 rounded-md border transition-colors ${
                active ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:bg-slate-50"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{p.name}</div>
                <div className={`text-xs ${active ? "text-slate-300" : "text-slate-500"} truncate`}>
                  {p.category || "—"}
                  {kwMatch && !nameMatches && (
                    <>
                      {" · "}
                      <span data-testid={`protocol-kw-match-${p.protocol_id}`}
                            className={active ? "font-medium text-amber-200" : "font-medium text-amber-700"}>
                        “{kwMatch}”
                      </span>
                    </>
                  )}
                </div>
              </div>
              <button data-testid={`fav-toggle-${p.protocol_id}`} onClick={(e) => toggleFav(p.protocol_id, e)} className="p-1 shrink-0">
                {isFav ? <Star size={18} weight="fill" className={active ? "text-yellow-300" : "text-yellow-500"} /> :
                         <Star size={18} className={active ? "text-slate-300" : "text-slate-400"} />}
              </button>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-2 text-sm text-slate-500 text-center py-6">
            Nessun protocollo trovato per "{q}".
          </div>
        )}
      </div>
    </div>
  );
}
